import styled from '@emotion/styled';
import {
  sdsColor,
  sdsPrimitiveColor,
  sdsRadius
} from '@staging-design-system/tokens';
import { rem } from '@utils';

export const DIAGRAM = {
  width: 410,
  height: 250,
  radius: 125,
  circleACenterX: 125,
  circleBCenterX: 285,
  overlapPath:
    'M 205 28.953 A 125 125 0 0 1 205 221.047 A 125 125 0 0 1 205 28.953'
} as const;

const Root = styled.span`
  display: flex;
  flex-direction: column;
  width: 100%;
`;

const Skeleton = styled.span`
  background: ${sdsPrimitiveColor.grey['200']};
  border-radius: ${sdsRadius.xSmall};
  display: inline-block;
  height: ${rem(20)};
  width: ${rem(140)};
`;

const ErrorText = styled.span`
  color: ${sdsColor.text.defaultSecondary};
  display: block;
  font-size: ${rem(13)};
  margin: ${rem(16)} 0;
  text-align: center;
`;

const Header = styled.span`
  font-size: ${rem(16)};
  font-weight: 600;
  margin-bottom: ${rem(16)};
`;

const Diagram = styled.span`
  aspect-ratio: ${DIAGRAM.width} /
    ${DIAGRAM.height};
  container-type: inline-size;
  display: block;
  isolation: isolate;
  margin-bottom: ${rem(24)};
  margin-inline: auto;
  max-width: ${rem(DIAGRAM.width)};
  position: relative;
  width: 100%;
`;

const VennSvg = styled.svg`
  display: block;
  height: 100%;
  overflow: visible;
  width: 100%;
`;

const AudienceContent = styled.span<{ isSecond?: boolean }>`
  align-items: center;
  color: ${({ isSecond }) =>
    isSecond ? sdsPrimitiveColor.mono.dark : sdsPrimitiveColor.mono.light};
  display: flex;
  flex-direction: column;
  justify-content: center;
  /* Position over corresponding circle center. */
  left: ${({ isSecond }) =>
    (
      (isSecond ? DIAGRAM.circleBCenterX : DIAGRAM.circleACenterX) /
        DIAGRAM.width
    ) * 100}%;
  max-width: ${rem(136)};
  pointer-events: none;
  position: absolute;
  text-align: center;
  top: 50%;
  transform: translate(-50%, -50%)
    /* Shift outward to give text more room away from the overlap. */
    ${({ isSecond }) => (isSecond ? 'translateX(30%)' : 'translateX(-30%)')};
`;

const CircleTitle = styled.span`
  display: block;
  font-size: ${rem(14)};
  font-weight: 400;
  line-height: ${rem(16)};
  margin-bottom: ${rem(12)};
  max-width: 72%;
  overflow-wrap: break-word;
  text-align: center;
`;

const CircleUniverse = styled.span`
  display: block;
  font-size: ${rem(20)};
  font-weight: 600;
  line-height: ${rem(32)};
  text-align: center;
`;

const CirclePercentage = styled.span`
  display: block;
  font-size: ${rem(12)};
  font-weight: 500;
  line-height: ${rem(16)};
  text-align: center;
`;

const OverlapContent = styled.span`
  align-items: center;
  color: ${sdsPrimitiveColor.mono.light};
  display: flex;
  flex-direction: column;
  justify-content: center;
  /* Position the overlap label between the two circle centers. */
  left: ${((DIAGRAM.circleACenterX + DIAGRAM.circleBCenterX) / 2 / DIAGRAM.width) * 100}%;
  max-width: ${rem(136)};
  pointer-events: none;
  position: absolute;
  text-align: center;
  top: 50%;
  transform: translate(-50%, -50%);
`;

const Dataset = styled.span`
  color: ${sdsColor.text.defaultSecondary};
  font-size: ${rem(12)};
  font-weight: 400;
  line-height: ${rem(16)};
  text-align: center;
  margin-bottom: ${rem(24)};
`;

const DatasetLabel = styled.span`
  font-weight: 600;
`;

const Keys = styled.span`
  margin: 0 auto;
`;

export default {
  Root,
  Header,
  Dataset,
  Diagram,
  VennSvg,
  AudienceContent,
  CircleTitle,
  CircleUniverse,
  CirclePercentage,
  OverlapContent,
  DatasetLabel,
  Keys,
  ErrorText,
  Skeleton
};
